import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TableSortLabel,
  Tooltip,
  Button,
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import EmailIcon from "@mui/icons-material/Email";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import {
  TABLE_STYLES,
  TABLE_ROW_HOVER_SX,
} from "../../../theme/tablestyles";
import { SELECT_MENU_PROPS } from "../../../theme/filterstyles";

import { formatPhone } from "../../../utils/Phone";

const NAME_COLUMN_WIDTH = 280;
const COMPANY_COLUMN_WIDTH = 240;
const JOB_TITLE_COLUMN_WIDTH = 200;
const PHONE_COLUMN_WIDTH = 150;
const EMAIL_COLUMN_WIDTH = 90;
const PROFILE_COLUMN_WIDTH = 90;

const HEAD_CELL_SX = {
  backgroundColor: "#00336C",
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#00336C",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};

const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";
const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": {
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

export default function BREContactsTable({
  hasContacts,
  embedded,

  selectedIds,
  allSelected,
  someSelected,
  toggleOne,
  togglePage,

  navigate,

  sortedVisibleContacts,
  orderBy,
  orderDirection,
  onSort,
  companyFilter,
  onCompanyFilterChange,
  uniqueCompanyNames,
  jobTitleFilter,
  onJobTitleFilterChange,
  uniqueJobTitles,

  setActiveContact,
  setProfileOpen,
}) {
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const openFilterMenu = (event, filterKey) => {
    setFilterAnchor(event.currentTarget);
    setActiveFilter(filterKey);
    setFilterSearch("");
  };

  const closeFilterMenu = () => {
    setFilterAnchor(null);
    setActiveFilter(null);
    setFilterSearch("");
  };

  const filteredCompanyOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return uniqueCompanyNames;
    return uniqueCompanyNames.filter((option) =>
      option.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch, uniqueCompanyNames]);

  const filteredJobTitleOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return uniqueJobTitles;
    return uniqueJobTitles.filter((option) =>
      option.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch, uniqueJobTitles]);

  const renderFilterHeader = ({
    label,
    filterKey,
    sortable = false,
    sortKey = filterKey,
    active = false,
  }) => {
    const minWidthByFilterKey = {
      company: 132,
      job_title: 140,
    };

    return (
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <Button
            variant="text"
            onClick={(event) => openFilterMenu(event, filterKey)}
            sx={{
              ...HEADER_TRIGGER_SX,
              minWidth: minWidthByFilterKey[filterKey] || HEADER_TRIGGER_SX.minWidth,
              color: active ? "#648315" : "#00336C",
              border: active ? "1.5px solid #648315" : HEADER_TRIGGER_SX.border,
              backgroundColor: "rgba(255,255,255,0.92)",
              "&:hover": { backgroundColor: "#ffffff", borderColor: active ? "#648315" : "rgba(255,255,255,0.8)" },
            }}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
          >
            {label}
          </Button>
          {sortable && (
            <TableSortLabel
              active={orderBy === sortKey}
              direction={orderBy === sortKey ? orderDirection : "asc"}
              onClick={() => onSort(sortKey)}
              sx={{
                position: "absolute",
                left: "100%",
                top: "50%",
                transform: "translateY(-50%)",
                ml: 0.5,
                "& .MuiTableSortLabel-icon": {
                  fontSize: "1rem",
                },
              }}
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    const commonMenuProps = {
      open: Boolean(filterAnchor),
      anchorEl: filterAnchor,
      onClose: closeFilterMenu,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      marginThreshold: 16,
      MenuListProps: {
        dense: true,
        sx: {
          py: 0,
          maxHeight: 520,
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      },
      ...SELECT_MENU_PROPS,
      slotProps: {
        ...SELECT_MENU_PROPS.slotProps,
      },
      PaperProps: {
        ...SELECT_MENU_PROPS.PaperProps,
        sx: {
          ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          maxHeight: 560,
          overflowX: "hidden",
          overflowY: "auto",
          py: 0,
          "& .MuiTextField-root": {
            maxWidth: "100%",
          },
        },
      },
    };

    const searchField = (placeholder) => (
      <Box sx={SEARCH_FIELD_SX}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={filterSearch}
          onChange={(event) => setFilterSearch(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={placeholder}
          sx={FILTER_SEARCH_INPUT_SX}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
            },
          }}
        />
      </Box>
    );

    if (activeFilter === "company") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search company")}
          <MenuItem
            selected={!companyFilter}
            onClick={() => {
              onCompanyFilterChange("");
              closeFilterMenu();
            }}
          >
            All Companies
          </MenuItem>
          {filteredCompanyOptions.map((option) => (
            <MenuItem
              key={option}
              selected={companyFilter === option}
              onClick={() => {
                onCompanyFilterChange(option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "job_title") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search job titles")}
          <MenuItem
            selected={!jobTitleFilter}
            onClick={() => {
              onJobTitleFilterChange("");
              closeFilterMenu();
            }}
          >
            All Job Titles
          </MenuItem>
          {filteredJobTitleOptions.map((option) => (
            <MenuItem
              key={option}
              selected={jobTitleFilter === option}
              onClick={() => {
                onJobTitleFilterChange(option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    return null;
  };

  if (!hasContacts) {
    return (
      <Box
        sx={{
          mt: 4,
          py: 6,
          textAlign: "center",
          border: "1px dashed #ccc",
          borderRadius: 2,
        }}
      >
        <Typography variant="h6">No contacts found</Typography>
        <Typography variant="body2" color="text.secondary">
          There are no contacts associated with this company.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer
        component="div"
        sx={{
          background: "#00336C",
          overflow: "hidden",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: { xs: 1180, md: 1320 },
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
        {/* ===== HEAD ===== */}
          <TableHead>
            <TableRow>
              {!embedded && (
                <TableCell
                  align="center"
                  padding="checkbox"
                  sx={HEAD_CELL_SX}
                >
                  <Checkbox
                    indeterminate={someSelected && !allSelected}
                    checked={allSelected}
                    onChange={togglePage}
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    indeterminateIcon={<RemoveCircleIcon />}
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                    }}
                  />
                </TableCell>
              )}

              {[
                { id: "name", label: "Name", sortable: true },
                { id: "company", label: "Company", sortable: true },
                { id: "job_title", label: "Job Title", sortable: true },
                { id: "direct_phone", label: "Direct Phone" },
                { id: "mobile_phone", label: "Mobile Phone" },
                { id: "email", label: "Email" },
                { id: "profile", label: "Profile" },
              ].map((col) => (
                <TableCell
                  key={col.id}
                  align="center"
                  sx={{
                    ...HEAD_CELL_SX,
                    ...(col.id === "name" && {
                      width: NAME_COLUMN_WIDTH,
                      maxWidth: NAME_COLUMN_WIDTH,
                    }),
                    ...(col.id === "company" && {
                      width: COMPANY_COLUMN_WIDTH,
                      maxWidth: COMPANY_COLUMN_WIDTH,
                    }),
                    ...(col.id === "job_title" && {
                      width: JOB_TITLE_COLUMN_WIDTH,
                      maxWidth: JOB_TITLE_COLUMN_WIDTH,
                    }),
                    ...((col.id === "direct_phone" || col.id === "mobile_phone") && {
                      width: PHONE_COLUMN_WIDTH,
                      maxWidth: PHONE_COLUMN_WIDTH,
                    }),
                    ...(col.id === "email" && {
                      width: EMAIL_COLUMN_WIDTH,
                      maxWidth: EMAIL_COLUMN_WIDTH,
                    }),
                    ...(col.id === "profile" && {
                      width: PROFILE_COLUMN_WIDTH,
                      maxWidth: PROFILE_COLUMN_WIDTH,
                    }),
                  }}
                >
                  {col.id === "company"
                    ? renderFilterHeader({
                        label: col.label,
                        filterKey: "company",
                        sortable: true,
                        active: Boolean(companyFilter),
                        sortKey: "company",
                      })
                    : col.id === "job_title"
                      ? renderFilterHeader({
                          label: col.label,
                          filterKey: "job_title",
                          sortable: true,
                          active: Boolean(jobTitleFilter),
                          sortKey: "job_title",
                        })
                      : col.sortable ? (
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? orderDirection : "asc"}
                            onClick={() => onSort(col.id)}
                            sx={{
                              color: "#ffffff !important",
                              "&:hover": { color: "#ffffff !important" },
                              "&.Mui-active": { color: "#ffffff !important" },
                              "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                            }}
                          >
                            {col.label}
                          </TableSortLabel>
                        ) : (
                          col.label
                        )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

        {/* ===== BODY ===== */}
          <TableBody sx={{ background: "white" }}>
            {sortedVisibleContacts.map((contact, index) => (
              <TableRow
                key={contact.id}
                sx={{ ...TABLE_ROW_HOVER_SX, backgroundColor: index % 2 === 0 ? "#fafafa" : "white", "&:hover": { backgroundColor: "#eef2f7" } }}
              >
                {!embedded && (
                  <TableCell align="center" padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(String(contact.id))}
                      onChange={() => toggleOne(String(contact.id))}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>
                )}

                {/* Name */}
                <TableCell
                  sx={{ ...TABLE_STYLES.bodyCell, width: NAME_COLUMN_WIDTH, maxWidth: NAME_COLUMN_WIDTH }}
                  align="center"
                >
                  <Tooltip title={contact.name || ""}>
                    <Button
                      variant="text"
                      sx={TABLE_STYLES.clickableCell}
                      onClick={() => navigate(`/bre-contacts/${contact.id}`)}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: NAME_COLUMN_WIDTH - 40,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {contact.name}
                      </span>
                    </Button>
                  </Tooltip>
                </TableCell>

                {/* Company */}
                <TableCell
                  sx={{ ...TABLE_STYLES.bodyCell, width: COMPANY_COLUMN_WIDTH, maxWidth: COMPANY_COLUMN_WIDTH }}
                  align="center"
                >
                  {contact.company ? (
                    <Tooltip title={contact.company.name}>
                      <Button
                        variant="text"
                        sx={{ ...TABLE_STYLES.clickableSubCell, fontSize: TABLE_STYLES.bodyCell.fontSize }}
                        onClick={() =>
                          navigate(`/bre-companies/${contact.company.id}`)
                        }
                      >
                        <span
                          style={{
                            display: "inline-block",
                            maxWidth: COMPANY_COLUMN_WIDTH - 40,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {contact.company.name}
                        </span>
                      </Button>
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                </TableCell>

                {/* Job Title */}
                <TableCell
                  sx={{ ...TABLE_STYLES.bodyCell, width: JOB_TITLE_COLUMN_WIDTH, maxWidth: JOB_TITLE_COLUMN_WIDTH }}
                  align="center"
                >
                  <Tooltip title={contact.job_title || ""}>
                    <span
                      style={{
                        display: "inline-block",
                        maxWidth: JOB_TITLE_COLUMN_WIDTH - 24,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                      }}
                    >
                      {contact.job_title || "—"}
                    </span>
                  </Tooltip>
                </TableCell>

                {/* Direct Phone */}
                <TableCell
                  sx={{ ...TABLE_STYLES.bodyCell, width: PHONE_COLUMN_WIDTH, maxWidth: PHONE_COLUMN_WIDTH }}
                  align="center"
                >
                  {contact.direct_phone_number
                    ? formatPhone(contact.direct_phone_number)
                    : "—"}
                </TableCell>

                {/* Mobile */}
                <TableCell
                  sx={{ ...TABLE_STYLES.bodyCell, width: PHONE_COLUMN_WIDTH, maxWidth: PHONE_COLUMN_WIDTH }}
                  align="center"
                >
                  {contact.cell_phone_number
                    ? formatPhone(contact.cell_phone_number)
                    : "—"}
                </TableCell>

                {/* Email */}
                <TableCell
                  sx={{ ...TABLE_STYLES.bodyCell, width: EMAIL_COLUMN_WIDTH, maxWidth: EMAIL_COLUMN_WIDTH }}
                  align="center"
                >
                  {contact.email ? (
                    <Tooltip title={contact.email}>
                      <IconButton
                        component="a"
                        href={`mailto:${contact.email}`}
                        sx={{
                          color: "#648315",
                          "&:hover": {
                            backgroundColor: "rgba(100,131,21,0.08)",
                          },
                        }}
                      >
                        <EmailIcon sx={{ fontSize: 26 }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <EmailIcon
                      sx={{
                        fontSize: 26,
                        color: "#648315",
                        opacity: 0.3,
                      }}
                    />
                  )}
                </TableCell>

                {/* Profile */}
                <TableCell
                  sx={{ ...TABLE_STYLES.bodyCell, width: PROFILE_COLUMN_WIDTH, maxWidth: PROFILE_COLUMN_WIDTH }}
                  align="center"
                >
                  <IconButton
                    onClick={() => {
                      setActiveContact(contact);
                      setProfileOpen(true);
                    }}
                  >
                    <AccountCircleIcon sx={{ color: "#648315", fontSize: 32 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {renderFilterMenu()}
    </>
  );
}
